/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { computed } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { inBetween } from 'src/luis/modules/labeling/utils/generalUtils';
import { TokenSelectionStore } from 'src/luis/modules/labeling/utils/TokenSelectionStore';
import { TokenUIProps } from 'src/modules/labeling/components/Line';
import { TokenRenderer } from 'src/modules/labeling/components/Renderers/TokenRenderer';
import { useTokenLabelingEventListeners } from 'src/modules/labeling/hooks/useLabelingEventListeners';
import { ViewOptions } from 'src/modules/labeling/types/ViewOptions';
import { tokenIndexDataAttribute } from 'src/modules/labeling/utils/labelingConstants';

export type TokenProps = TokenUIProps & {
    /**
     * Children to be rendered.
     */
    children: React.ReactNode;

    /**
     * Array of booleans indicating the state of every token
     * whether it has a conflict or not.
     */
    conflicts: boolean[];

    /**
     * Registers the labeling event listeners.
     */
    tokenEventListeners: ReturnType<typeof useTokenLabelingEventListeners>;

    /**
     * Index of the current token.
     */
    tokenIndex: number;

    /**
     * Labeling view options.
     */
    viewOptions: ViewOptions;

    /**
     * Whether the utterance is RTL or not.
     */
    isRTL: boolean;

    /**
     * Mobx store that contains selection states.
     */
    tokenSelectionStore: TokenSelectionStore;

    /**
     * Make visual tweaks to the labeler to make
     * it compatible with char tokenization.
     */
    useCharTokenization: boolean;
};

export const Token = observer((props: TokenProps) => {
    const { tokenEventListeners, tokenIndex, tokenSelectionStore, conflicts, ...rest } = props;
    const ref = React.useRef<HTMLSpanElement>();

    /**
     * The combination of React.useMemo + computed is used for the following reasons:
     * computed: to subscribe to the granular states and update only when relevant states change,
     * which prevents all the tokens from updating whenever the selection changes.
     * useMemo: is used to preserve the computed instances, this is more of micro optimization
     * and most probably won't matter that much in most cases. Removing useMemo shouldn't hurt
     * the performance in any obvious way.
     */
    const inBetweenSelection = React.useMemo(
        () => computed(() => inBetween(tokenSelectionStore.selection[0], tokenSelectionStore.selection[1])(tokenIndex)),
        [tokenSelectionStore]
    );
    const hasStartBracket = React.useMemo(
        () => computed(() => tokenSelectionStore.selection[0] === tokenIndex || tokenSelectionStore.hoverSelection[0] === tokenIndex),
        [tokenSelectionStore]
    );
    const hasEndBracket = React.useMemo(
        () => computed(() => tokenSelectionStore.selection[1] === tokenIndex || tokenSelectionStore.hoverSelection[1] === tokenIndex),
        [tokenSelectionStore]
    );

    const dataAttributes = { [tokenIndexDataAttribute]: `${tokenIndex}` };

    return (
        <TokenRenderer
            {...tokenEventListeners(tokenIndex)}
            {...dataAttributes}
            {...rest}
            ref={ref}
            data-is-focusable="true"
            hasStartBracket={hasStartBracket.get()}
            hasEndBracket={hasEndBracket.get()}
            selected={inBetweenSelection.get()}
            hasConflict={conflicts[tokenIndex]}
        />
    );
});
